import React, { useMemo, useState } from 'react';
import { NavBar } from '../components/NavBar';
import { Button } from '../components/SubscriptionAppButton';
import { Badge } from '../components/SubscriptionAppBadge';
import { Card } from '../components/SubscriptionAppCard';
import { SearchBar } from '../components/SearchBar';
import { TableFilter, Filter } from '../components/TableFilter';
import { ExportButton } from '../components/ExportButton';
import { Pagination } from '../components/SubscriptionAppPagination';
import { Plus, Download, MoreVertical, Calendar, Users, Wine, Trash2, XCircle, CheckCircle, Layers } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
// Mock Data
interface Allocation {
  id: string;
  name: string;
  description: string;
  status: 'Draft' | 'Active' | 'Closed';
  productCount: number;
  startDate: string;
  endDate: string;
  claimed: number;
  totalAvailable: number;
  revenue: number;
  eligibleCount: number;
}
const mockAllocations: Allocation[] = [{
  id: '1',
  name: '2023 Reserve Shiraz Release',
  description: 'Exclusive access to our flagship red for Gold members',
  status: 'Active',
  productCount: 1,
  startDate: '1 Mar 2026',
  endDate: '15 Mar 2026',
  claimed: 142,
  totalAvailable: 200,
  revenue: 28400,
  eligibleCount: 485
}, {
  id: '2',
  name: 'Autumn Library Collection',
  description: 'Rare back-vintage wines from the cellar',
  status: 'Draft',
  productCount: 4,
  startDate: '20 Mar 2026',
  endDate: '10 Apr 2026',
  claimed: 0,
  totalAvailable: 350,
  revenue: 0,
  eligibleCount: 0
}, {
  id: '3',
  name: '2022 Single Vineyard Pinot',
  description: 'Limited release from the Block 4 vineyard',
  status: 'Closed',
  productCount: 1,
  startDate: '10 Jan 2026',
  endDate: '25 Jan 2026',
  claimed: 120,
  totalAvailable: 120,
  revenue: 14400,
  eligibleCount: 320
}, {
  id: '4',
  name: 'Summer Rosé Pack',
  description: '3-pack special for the holiday season',
  status: 'Closed',
  productCount: 3,
  startDate: '1 Dec 2025',
  endDate: '20 Dec 2025',
  claimed: 89,
  totalAvailable: 150,
  revenue: 8010,
  eligibleCount: 210
}, {
  id: '5',
  name: 'Vintner Exclusive — Museum Release',
  description: 'Aged Cabernet for our top tier members only',
  status: 'Active',
  productCount: 2,
  startDate: '5 Mar 2026',
  endDate: '30 Mar 2026',
  claimed: 18,
  totalAvailable: 50,
  revenue: 5400,
  eligibleCount: 50
}, {
  id: '6',
  name: 'New Vintage Tasting Pack',
  description: 'Sample the upcoming releases before anyone else',
  status: 'Draft',
  productCount: 6,
  startDate: 'TBD',
  endDate: '',
  claimed: 0,
  totalAvailable: 500,
  revenue: 0,
  eligibleCount: 0
}];
export function AllocationsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({
    status: [],
    dateRange: [],
    products: [],
    segment: []
  });
  const [openFilterDropdowns, setOpenFilterDropdowns] = useState<Set<string>>(new Set());
  const [addedFilters, setAddedFilters] = useState<Set<string>>(new Set());
  // Filter Configuration
  const availableFilters: Filter[] = [{
    id: 'status',
    label: 'Status',
    options: [{
      id: 'draft',
      label: 'Draft',
      value: 'Draft'
    }, {
      id: 'active',
      label: 'Active',
      value: 'Active'
    }, {
      id: 'closed',
      label: 'Closed',
      value: 'Closed'
    }],
    selectedValues: filterValues.status || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('status')
  }, {
    id: 'dateRange',
    label: 'Date Range',
    options: [{
      id: 'upcoming',
      label: 'Upcoming',
      value: 'upcoming'
    }, {
      id: 'active',
      label: 'Currently Active',
      value: 'active'
    }, {
      id: 'past',
      label: 'Past',
      value: 'past'
    }],
    selectedValues: filterValues.dateRange || [],
    multiSelect: false,
    isOpen: openFilterDropdowns.has('dateRange')
  }, {
    id: 'products',
    label: 'Products',
    options: [{
      id: '1',
      label: 'Single Product',
      value: '1'
    }, {
      id: 'multi',
      label: 'Multi-Product',
      value: 'multi'
    }],
    selectedValues: filterValues.products || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('products')
  }, {
    id: 'segment',
    label: 'Segment',
    options: [{
      id: 'gold',
      label: 'Gold Members',
      value: 'gold'
    }, {
      id: 'silver',
      label: 'Silver Members',
      value: 'silver'
    }, {
      id: 'club',
      label: 'Club Members',
      value: 'club'
    }],
    selectedValues: filterValues.segment || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('segment')
  }];
  // Filter Logic
  const filteredAllocations = useMemo(() => {
    return mockAllocations.filter(allocation => {
      // Search
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!allocation.name.toLowerCase().includes(query) && !allocation.description.toLowerCase().includes(query)) {
          return false;
        }
      }
      // Status Filter
      if (filterValues.status && filterValues.status.length > 0) {
        if (!filterValues.status.includes(allocation.status)) return false;
      }
      return true;
    });
  }, [searchQuery, filterValues]);
  // Handlers
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedIds(new Set(filteredAllocations.map(a => a.id)));
    } else {
      setSelectedIds(new Set());
    }
  };
  const handleSelectRow = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };
  const handleFilterChange = (filterId: string, value: string | string[]) => {
    setFilterValues(prev => ({
      ...prev,
      [filterId]: Array.isArray(value) ? value : [value]
    }));
  };
  const handleFilterRemove = (filterId: string) => {
    const newAdded = new Set(addedFilters);
    newAdded.delete(filterId);
    setAddedFilters(newAdded);
    setFilterValues(prev => ({
      ...prev,
      [filterId]: []
    }));
  };
  const handleToggleFilterDropdown = (filterId: string) => {
    const newOpen = new Set(openFilterDropdowns);
    if (newOpen.has(filterId)) {
      newOpen.delete(filterId);
    } else {
      newOpen.add(filterId);
    }
    setOpenFilterDropdowns(newOpen);
  };
  const handleAddFilter = (filterId: string) => {
    const newAdded = new Set(addedFilters);
    newAdded.add(filterId);
    setAddedFilters(newAdded);
  };
  const handleClearAllFilters = () => {
    setFilterValues({});
    setAddedFilters(new Set());
    setSearchQuery('');
  };
  // Helper for progress bar color
  const getProgressColor = (claimed: number, total: number) => {
    if (total === 0) return 'bg-ocean-gray-200';
    const percentage = claimed / total * 100;
    if (percentage > 95) return 'bg-[#EF4444]'; // Red
    if (percentage >= 80) return 'bg-[#F59E0B]'; // Amber
    return 'bg-[#10B981]'; // Green
  };
  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Draft':
        return 'default';
      // Grey
      case 'Closed':
        return 'default';
      // Slate/Grey
      default:
        return 'default';
    }
  };
  // Bulk Actions Logic
  const selectedAllocations = mockAllocations.filter(a => selectedIds.has(a.id));
  const hasActiveSelected = selectedAllocations.some(a => a.status === 'Active');
  const hasDraftSelected = selectedAllocations.some(a => a.status === 'Draft');
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-4240">
      {/* NavBar */}
      <div className="mb-6" data-id="element-4241">
        <NavBar tabs={[]} title="Allocations" data-id="element-4242" />
      </div>

      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-4243">
        <div className="flex-1" data-id="element-4244">
          {/* Breadcrumbs or description could go here */}
        </div>
        <div className="flex items-center gap-3" data-id="element-4245">
          <ExportButton onExport={() => console.log('Exporting allocations')} data-id="element-4246" />
          <Button variant="primary" iconLeft={<Plus className="w-4 h-4" data-id="element-4248" />} onClick={() => navigate('/allocations/create')} data-id="element-4247">
            Create Allocation
          </Button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg mb-6 p-4 shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-4249">
        <div className="flex flex-col gap-4" data-id="element-4250">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4" data-id="element-4251">
            <div className="w-full lg:w-80 flex-shrink-0" data-id="element-4252">
              <SearchBar placeholder="Search allocations..." value={searchQuery} onChange={setSearchQuery} className="w-full" data-id="element-4253" />
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-shrink-0 w-full sm:w-auto sm:ml-auto" data-id="element-4254">
              <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyButton={true} data-id="element-4255" />
            </div>
          </div>

          {addedFilters.size > 0 && <div className="flex items-center gap-3" data-id="element-4256">
              <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyChips={true} data-id="element-4257" />
              <button onClick={handleClearAllFilters} className="text-[13px] text-[#0EA5E9] hover:text-[#0284C7] font-medium transition-colors" data-id="element-4258">
                Clear all
              </button>
            </div>}
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && <div className="bg-white border border-[#E2E8F0] rounded-lg px-6 py-3 mb-6 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-4259">
          <div className="flex items-center gap-2" data-id="element-4260">
            <span className="text-[14px] font-medium text-[#0F172A]" data-id="element-4261">
              {selectedIds.size} allocation{selectedIds.size !== 1 ? 's' : ''}{' '}
              selected
            </span>
            <div className="h-4 w-px bg-[#E2E8F0] mx-2" data-id="element-4262" />
            <button onClick={() => setSelectedIds(new Set())} className="text-[13px] text-[#64748B] hover:text-[#0F172A] transition-colors" data-id="element-4263">
              Clear selection
            </button>
          </div>
          <div className="flex items-center gap-3" data-id="element-4264">
            <Button variant="outline" size="sm" iconLeft={<Download className="w-4 h-4" data-id="element-4266" />} data-id="element-4265">
              Export Selected
            </Button>
            {hasActiveSelected && <Button variant="outline" size="sm" iconLeft={<XCircle className="w-4 h-4" data-id="element-4268" />} data-id="element-4267">
                Close Allocations
              </Button>}
            {hasDraftSelected && <Button variant="danger" size="sm" iconLeft={<Trash2 className="w-4 h-4" data-id="element-4270" />} data-id="element-4269">
                Delete
              </Button>}
          </div>
        </div>}

      {/* Data Table */}
      <Card padding="none" className="overflow-hidden" data-id="element-4271">
        <div className="overflow-x-auto" data-id="element-4272">
          <table className="w-full" data-id="element-4273">
            <thead className="bg-[#F1F5F9]" data-id="element-4274">
              <tr data-id="element-4275">
                <th className="w-12 px-6 py-3 border-b border-[#E2E8F0]" data-id="element-4276">
                  <div className="flex items-center justify-center" data-id="element-4277">
                    <input type="checkbox" className="w-[18px] h-[18px] rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9] cursor-pointer" checked={selectedIds.size === filteredAllocations.length && filteredAllocations.length > 0} onChange={handleSelectAll} data-id="element-4278" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-4279">
                  Allocation
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-4280">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-4281">
                  Products
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-4282">
                  Window
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-4283">
                  Claimed
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-4284">
                  Revenue
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-4285">
                  Eligible
                </th>
                <th className="px-6 py-3 w-10" data-id="element-4286"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8F0]" data-id="element-4287">
              {filteredAllocations.map(allocation => {
              const isSelected = selectedIds.has(allocation.id);
              const percentage = allocation.totalAvailable > 0 ? Math.round(allocation.claimed / allocation.totalAvailable * 100) : 0;
              return <tr key={allocation.id} className={`hover:bg-[#F8FAFC] transition-colors ${isSelected ? 'bg-[#F0F9FF]' : ''}`} data-id="element-4288">
                    <td className="px-6 py-4" data-id="element-4289">
                      <div className="flex items-center justify-center" data-id="element-4290">
                        <input type="checkbox" className="w-[18px] h-[18px] rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9] cursor-pointer" checked={isSelected} onChange={() => handleSelectRow(allocation.id)} data-id="element-4291" />
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-4292">
                      <div data-id="element-4293">
                        <button onClick={() => navigate(`/allocations/${allocation.id}`)} className="text-[14px] font-bold text-[#0F172A] hover:text-[#0EA5E9] transition-colors text-left" data-id="element-4294">
                          {allocation.name}
                        </button>
                        <div className="text-[13px] text-[#64748B] mt-0.5 truncate max-w-[280px]" data-id="element-4295">
                          {allocation.description}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-4296">
                      <Badge variant={getStatusVariant(allocation.status)} data-id="element-4297">
                        {allocation.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4" data-id="element-4298">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#F1F5F9] text-[#475569] border border-[#E2E8F0]" data-id="element-4299">
                        {allocation.productCount}{' '}
                        {allocation.productCount === 1 ? 'wine' : 'wines'}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-4300">
                      <div className="text-[14px] text-[#0F172A]" data-id="element-4301">
                        {allocation.startDate === 'TBD' ? 'TBD' : <>
                            {allocation.startDate}{' '}
                            {allocation.endDate && `– ${allocation.endDate}`}
                          </>}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-4302">
                      <div className="w-full max-w-[140px]" data-id="element-4303">
                        <div className="flex justify-between text-[13px] mb-1" data-id="element-4304">
                          <span className="font-medium text-[#0F172A]" data-id="element-4305">
                            {allocation.claimed} / {allocation.totalAvailable}
                          </span>
                          <span className="text-[#64748B]" data-id="element-4306">{percentage}%</span>
                        </div>
                        <div className="h-1.5 w-full bg-[#E2E8F0] rounded-full overflow-hidden" data-id="element-4307">
                          <div className={`h-full rounded-full ${getProgressColor(allocation.claimed, allocation.totalAvailable)}`} style={{
                        width: `${percentage}%`
                      }} data-id="element-4308" />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-4309">
                      <span className="text-[14px] font-medium text-[#0F172A]" data-id="element-4310">
                        ${allocation.revenue.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-4311">
                      <span className="text-[14px] text-[#475569]" data-id="element-4312">
                        {allocation.eligibleCount}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-4313">
                      <button className="text-[#94A3B8] hover:text-[#0F172A] transition-colors" data-id="element-4314">
                        <MoreVertical className="w-4 h-4" data-id="element-4315" />
                      </button>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-[#E2E8F0]" data-id="element-4316">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-4317">
            <div className="text-[13px] text-[#64748B]" data-id="element-4318">
              Showing 1–{filteredAllocations.length} of{' '}
              {filteredAllocations.length} allocations
            </div>
            <div className="flex items-center gap-2" data-id="element-4319">
              <button disabled className="px-4 py-2 border border-[#CBD5E1] rounded-md text-[13px] font-medium text-[#475569] hover:bg-[#F8FAFC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-4320">
                Previous
              </button>
              <div className="flex items-center gap-1" data-id="element-4321">
                <button className="w-10 h-10 rounded-md text-[13px] font-medium bg-[#0EA5E9] text-white transition-colors" data-id="element-4322">
                  1
                </button>
              </div>
              <button disabled className="px-4 py-2 border border-[#CBD5E1] rounded-md text-[13px] font-medium text-[#475569] hover:bg-[#F8FAFC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-4323">
                Next
              </button>
            </div>
          </div>
        </div>
      </Card>
    </div>;
}